import { Box, Stack, Tooltip, useTheme } from "@mui/material";
import { BackIcon } from "assets/images/onboarding/icons";
import { Button } from "components/button";
import { Provider } from "jotai";
import { getStepperSegment, stepConfig } from "../constants/steps";
import { useOnboardingModal } from "../hooks/useOnboardingModal";
import { onboardingModalStore } from "../store/onboardingModalStore";
import { OnboardingCurrentTool } from "./OnboardingCurrentTool";
import { OnboardingImprovements } from "./OnboardingImprovements";
import { OnboardingIntegrations } from "./OnboardingIntegrations";
import { OnboardingOrgType } from "./OnboardingOrgType";
import { OnboardingReferral } from "./OnboardingReferral";
import { OnboardingTeamSize } from "./OnboardingTeamSize";
import { OnboardingWelcome } from "./OnboardingWelcome";
import { OnboardingStepStepper } from "./shared/OnboardingStepStepper";

interface OnboardingModalBodyProps {
  onClose: () => void;
}

const OnboardingModalContent = ({
  onClose,
}: OnboardingModalBodyProps): JSX.Element => {
  const theme = useTheme();
  const {
    currentStep,
    canContinue,
    goNext,
    goBack,
    submitAndClose,
    isSubmitting,
  } = useOnboardingModal();

  const config = stepConfig[currentStep];
  const stepperSegment = getStepperSegment(currentStep);

  const handleContinue = (): void => {
    if (currentStep === 6) {
      submitAndClose(onClose);
    } else {
      goNext();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        color: theme.palette.darkBg.medium,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <OnboardingStepStepper activeStep={stepperSegment} />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {currentStep === 0 && <OnboardingWelcome />}
        {currentStep === 1 && <OnboardingOrgType />}
        {currentStep === 2 && <OnboardingCurrentTool />}
        {currentStep === 3 && <OnboardingIntegrations />}
        {currentStep === 4 && <OnboardingTeamSize />}
        {currentStep === 5 && <OnboardingImprovements />}
        {currentStep === 6 && <OnboardingReferral />}
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent={config.fullWidthCta ? "stretch" : "flex-end"}
        alignItems="center"
        gap={config.fullWidthCta ? "12px" : "8px"}
        sx={{
          marginTop: "14px",
          flexShrink: 0,
          paddingBottom: config.fullWidthCta ? "40px" : "16px",
          marginRight: config.fullWidthCta ? 0 : "-8px",
        }}
      >
        {config.showBack && (
          <Button
            variant="ghost"
            onClick={goBack}
            sx={{
              position: "relative",
              color: theme.palette.primitive.neutrals.neutral["600"],
              borderColor: theme.palette.primitive.neutrals.neutral["600"],
              border: "1px solid",
              borderRadius: "6px",
              padding: "8px 24px",
              minWidth: "120px",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: theme.palette.primitive.neutrals.neutral[800],
              },
            }}
          >
            <BackIcon
              sx={{
                width: "24px",
                height: "24px",
                position: "absolute",
                left: "12px",
                color: "inherit",
              }}
            />
            Back
          </Button>
        )}
        <Tooltip
          title={
            !canContinue && currentStep === 3
              ? "Select options in at least two sections to continue"
              : ""
          }
          arrow
          placement="top-end"
          componentsProps={{
            tooltip: {
              sx: {
                typography: "body-xsmall",
                backgroundColor: theme.palette.neutral["020"],
                color: theme.palette.primitive.neutrals.neutral[900],
                padding: "4px 12px",
                borderRadius: "4px",
              },
            },
            arrow: {
              sx: {
                color: theme.palette.neutral["020"],
              },
            },
            popper: {
              modifiers: [
                {
                  name: "arrow",
                  options: {
                    padding: { right: 14 },
                  },
                },
              ],
            },
          }}
        >
          <span
            style={{
              flex: config.fullWidthCta ? 1 : "initial",
              display: "inline-flex",
            }}
          >
            <Button
              variant="primary"
              onClick={handleContinue}
              disabled={!canContinue}
              loading={isSubmitting}
              sx={{
                backgroundColor: theme.palette.secondary["purple-tint"],
                color: theme.palette.primitive.neutrals.neutral[900],
                borderRadius: "4px",
                padding: "8px 16px",
                fontWeight: 600,
                flex: 1,
                "&:hover": {
                  backgroundColor: theme.palette.secondary["purple-tint-2"],
                },
                "&.Mui-disabled": {
                  backgroundColor:
                    theme.palette.primitive.neutrals.neutral[800],
                  color: theme.palette.primitive.neutrals.neutral[700],
                },
              }}
            >
              {config.ctaLabel}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export const OnboardingModalBody = ({
  onClose,
}: OnboardingModalBodyProps): JSX.Element => {
  return (
    <Provider store={onboardingModalStore}>
      <OnboardingModalContent onClose={onClose} />
    </Provider>
  );
};
